import { useCallback, useMemo, useRef, useState } from 'react';
import type { NavigationProp } from '@react-navigation/native';
import { addSentryBreadcrumb } from '../../../shared/sentry/sentryUser';
import type { Product } from '../../../catalog/domain-api';
import { listVariantsForProduct } from '../../../catalog/infra-api'
import { productRequiresHourInput, productRequiresQuantityInput } from '../../../catalog/domain-api'
import { optionsPriceDelta, isComboValidityOutOfWindow } from '../../../catalog/domain-api';
import type { ProductVariant } from '../../../catalog/domain-api';
import { useMenuModifiersEnabled } from '../../../catalog/app-api'
import { fetchMenuOptionsForProducts } from '../../../catalog/infra-api'
import type { MenuOptionGroup, SelectedMenuOption } from '../../../catalog/domain-api';
import { peekCachedMenuOptions, readPersistedMenuOptions } from '../../../catalog/infra-api';
import type { AddProductToCartOptions, PackageCartInput } from '../../store/usePosStore';
import type { CartItem } from '../../store/usePosStore';
import { buildStockTapNoticeCopy } from '../../application/posCartStockIssuePresentation'
import { isProductCardOutOfStock } from '../../application/posScreen.utils'
import { buildPosVariantLineProduct } from '../../application/posVariantLineProduct';
import type { PosStockSalePolicy } from '../../domain/posTypes';
import { showInfo } from '../../../shared/ui/appToast';
import { hapticAddToCart } from '../../../shared/ui/posHaptics';

type StockTapNotice = { product: Product; policy: PosStockSalePolicy };

export function usePosProductPress(options: {
  canEdit: boolean;
  stockSalePolicy: PosStockSalePolicy;
  addProductToCart: (product: Product, quantityOrOpts?: number | AddProductToCartOptions) => void;
  addPackageToCart: (pkg: PackageCartInput, quantity?: number) => void;
  /** Gap 4: actualizar menu options de una línea existente. */
  updateCartLineMenuOptions: (lineId: string, menuOptions: SelectedMenuOption[] | undefined, lineNote?: string | null) => void;
  /** Gap 4: actualizar variante de una línea existente. */
  updateCartLineVariant: (lineId: string, variantId: string | null, name: string, price: number, sku?: string | null) => void;
  currentBranchId: string | null | undefined;
  navigation: NavigationProp<Record<string, object | undefined>>;
  setCheckoutVisible: (v: boolean) => void;
}) {
  const { canEdit, stockSalePolicy, addProductToCart, addPackageToCart, updateCartLineMenuOptions, updateCartLineVariant, currentBranchId, navigation, setCheckoutVisible } =
    options;

  const menuModifiersEnabled = useMenuModifiersEnabled();
  const [weightProduct, setWeightProduct] = useState<Product | null>(null);
  const [hourProduct, setHourProduct] = useState<Product | null>(null);
  const [variantPickerProduct, setVariantPickerProduct] = useState<Product | null>(null);
  const [variantPickerVariants, setVariantPickerVariants] = useState<ProductVariant[]>([]);
  const [pendingVariantId, setPendingVariantId] = useState<string | null>(null);
  const [menuOptionsProduct, setMenuOptionsProduct] = useState<Product | null>(null);
  const [menuOptionsGroups, setMenuOptionsGroups] = useState<MenuOptionGroup[]>([]);
  const [menuOptionsLoading, setMenuOptionsLoading] = useState(false);
  const [stockTapNotice, setStockTapNotice] = useState<StockTapNotice | null>(null);
  const [addedFlashProductId, setAddedFlashProductId] = useState<string | null>(null);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editingMenuOptions, setEditingMenuOptions] = useState<SelectedMenuOption[] | undefined>(undefined);
  const [editingLineNote, setEditingLineNote] = useState<string | null>(null);
  const addedFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProcessingRef = useRef(false);

  const flashAddedProduct = useCallback((productId: string) => {
    setAddedFlashProductId(productId);
    if (addedFlashTimerRef.current) clearTimeout(addedFlashTimerRef.current);
    addedFlashTimerRef.current = setTimeout(() => {
      setAddedFlashProductId(null);
      addedFlashTimerRef.current = null;
    }, 480);
  }, []);

  const openInventoryForProduct = useCallback(
    (product: Product) => {
      if (!currentBranchId) {
        showInfo('Seleccioná una sucursal', 'Elegí una sucursal para corregir el stock.');
        return;
      }
      const stockId = 'stockId' in product ? product.stockId : null;
      setStockTapNotice(null);
      setCheckoutVisible(false);
      navigation.navigate('Inventory', {
        focusToken: `${Date.now()}`,
        focusProductId: product.id,
        focusProductName: product.name,
        focusProductSku: product.sku ?? null,
        preferredAction: stockId ? 'adjust' : 'add',
      });
    },
    [currentBranchId, navigation, setCheckoutVisible]
  );

  const tryAddProduct = useCallback(
    async (p: Product) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      try {
      const addProduct = (product: Product, quantityOrOpts: number | AddProductToCartOptions = 1) => {
        hapticAddToCart();
        addProductToCart(product, quantityOrOpts);
        flashAddedProduct(product.id);
      };
      if (p.menuAvailable === false) {
        showInfo(
          'Pausado hoy',
          'Este ítem está pausado. Reactivalo en Catálogo → producto → «Disponible hoy».',
        );
        return;
      }
      if (isComboValidityOutOfWindow(p)) {
        showInfo(
          'Fuera de vigencia',
          'Este combo no está vigente hoy. Revisá la vigencia en Catálogo → Combos.',
        );
        return;
      }
      if (menuModifiersEnabled) {
        const cached = peekCachedMenuOptions(p.id);
        if (cached && cached.length > 0) {
          setMenuOptionsProduct(p);
          setMenuOptionsGroups(cached);
          setMenuOptionsLoading(false);
          return;
        }
        if (cached === null) {
          // Cache miss: fetch primero, abrir el modal solo si hay modifiers.
          // Antes abríamos el modal como loading sheet (PedidosYa-style), pero eso
          // producía un flash de "aparece y se cierra" para productos sin adicionales.
          try {
            const map = await fetchMenuOptionsForProducts([p.id]);
            const groups = map.get(p.id);
            if (groups && groups.length > 0) {
              setMenuOptionsProduct(p);
              setMenuOptionsGroups(groups);
              setMenuOptionsLoading(false);
              return;
            }
            if (groups && groups.length === 0) {
              // known no modifiers — continue to variants/base
            } else {
              // Offline + no disk: fail-closed (don't sell without size/extras).
              const disk = await readPersistedMenuOptions(p.id);
              if (disk && disk.length > 0) {
                setMenuOptionsProduct(p);
                setMenuOptionsGroups(disk);
                setMenuOptionsLoading(false);
                return;
              }
              showInfo(
                'Sin conexión',
                'No se pudieron cargar las opciones. Reintentá con internet.',
              );
              return;
            }
          } catch {
            const disk = await readPersistedMenuOptions(p.id);
            if (disk && disk.length > 0) {
              setMenuOptionsProduct(p);
              setMenuOptionsGroups(disk);
              setMenuOptionsLoading(false);
              return;
            }
            if (disk && disk.length === 0) {
              // known no modifiers on disk — continue
            } else {
              showInfo(
                'Sin conexión',
                'No se pudieron cargar las opciones. Reintentá con internet.',
              );
              return;
            }
          }
        }
        // cached === [] → known no modifiers; continue.
      }
      try {
        const variants = await listVariantsForProduct(p.id);
        if (variants.length > 0) {
          setVariantPickerProduct(p);
          setVariantPickerVariants(variants);
          return;
        }
      } catch (error) {
        // Sin variantes locales: seguir flujo normal.
        addSentryBreadcrumb('pos', 'listVariantsForProduct failed', 'warning', { productId: p.id, error: String(error) });
      }
      const measureFlags = {
        unitOfMeasure: p.unitOfMeasure ?? 'unit',
      };
      if (productRequiresHourInput(measureFlags)) {
        setHourProduct(p);
        return;
      }
      if (productRequiresQuantityInput(measureFlags)) {
        // Gap 1: cualquier UoM fraccional (kg, g, l, ml, m, cm) abre el modal de cantidad.
        setWeightProduct(p);
        return;
      }
      addProduct(p);
      } finally {
        isProcessingRef.current = false;
      }
    },
    [addProductToCart, flashAddedProduct, menuModifiersEnabled],
  );

  // After selecting a variant, continue the flow: weight → hour → addProduct.
  // Mirrors tryAddProduct's post-variant checks so weighable/hour variants
  // still prompt for input instead of being added with quantity: 1.
  const handleVariantSelect = useCallback(
    (variant: ProductVariant) => {
      const product = variantPickerProduct;
      if (!product) return;
      const variantProduct = buildPosVariantLineProduct(product, variant);
      const lineId = editingLineId;
      setVariantPickerProduct(null);
      setVariantPickerVariants([]);
      setEditingLineId(null);
      if (lineId) {
        // Gap 4: edit mode — actualizar variante de línea existente.
        hapticAddToCart();
        updateCartLineVariant(lineId, variant.id, variantProduct.name, variantProduct.price, variantProduct.sku);
        return;
      }
      setPendingVariantId(variant.id);
      const measureFlags = {
        unitOfMeasure: product.unitOfMeasure ?? 'unit',
      };
      if (productRequiresHourInput(measureFlags)) {
        setHourProduct(variantProduct);
        return;
      }
      if (productRequiresQuantityInput(measureFlags)) {
        setWeightProduct(variantProduct);
        return;
      }
      hapticAddToCart();
      addProductToCart(variantProduct, { quantity: 1, variantId: variant.id });
      setPendingVariantId(null);
      flashAddedProduct(product.id);
    },
    [addProductToCart, editingLineId, flashAddedProduct, updateCartLineVariant, variantPickerProduct],
  );

  const handleProductPress = useCallback(
    (product: Product) => {
      if (!canEdit) {
        showInfo('Modo solo lectura', 'Podés mirar el POS, no cobrar.');
        return;
      }
      const out = isProductCardOutOfStock(product);
      if (!out || stockSalePolicy === 'allow') {
        void tryAddProduct(product);
        return;
      }
      if (stockSalePolicy === 'block' || stockSalePolicy === 'warn') {
        setStockTapNotice({ product, policy: stockSalePolicy });
      }
    },
    [canEdit, stockSalePolicy, tryAddProduct],
  );

  /** Paquete de servicios: línea directa al carrito (sin variantes, stock ni adicionales). */
  const handlePackagePress = useCallback(
    (pkg: PackageCartInput) => {
      if (!canEdit) {
        showInfo('Modo solo lectura', 'Podés mirar el POS, no cobrar.');
        return;
      }
      hapticAddToCart();
      addPackageToCart(pkg, 1);
      flashAddedProduct(pkg.packageId);
    },
    [addPackageToCart, canEdit, flashAddedProduct],
  );

  const closeMenuOptions = useCallback(() => {
    setMenuOptionsProduct(null);
    setMenuOptionsGroups([]);
    setMenuOptionsLoading(false);
    setEditingLineId(null);
    setEditingMenuOptions(undefined);
    setEditingLineNote(null);
  }, []);

  const handleMenuOptionsConfirm = useCallback(
    (selected: SelectedMenuOption[], lineNote: string) => {
      const product = menuOptionsProduct;
      const lineId = editingLineId;
      closeMenuOptions();
      if (!product) return;
      hapticAddToCart();
      if (lineId) {
        // Gap 4: edit mode — actualizar línea existente.
        updateCartLineMenuOptions(lineId, selected, lineNote);
      } else {
        addProductToCart(product, { quantity: 1, menuOptions: selected, lineNote });
        flashAddedProduct(product.id);
      }
    },
    [addProductToCart, closeMenuOptions, editingLineId, flashAddedProduct, menuOptionsProduct, updateCartLineMenuOptions],
  );

  const handleAddOutOfStockProduct = useCallback(() => {
    if (!stockTapNotice) return;
    const product = stockTapNotice.product;
    const policy = stockTapNotice.policy;
    setStockTapNotice(null);
    const qty =
      'stockQuantity' in product && typeof product.stockQuantity === 'number'
        ? product.stockQuantity
        : null;
    const willGoNegative = qty != null && qty <= 0;
    showInfo(
      policy === 'warn' ? 'Agregado con aviso' : 'Agregado sin stock',
      willGoNegative
        ? 'El stock de este producto va a quedar en negativo.'
        : 'Revisá el inventario antes de cerrar el turno.',
    );
    // Re-enter full tryAdd (modifiers / variants / weight) — don't skip modal.
    void tryAddProduct(product);
  }, [stockTapNotice, tryAddProduct]);

  // Gap 4: re-abrir el modal de menu options sobre una línea existente.
  const openMenuOptionsEditor = useCallback(
    async (line: CartItem) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      try {
        // Reconstruir un Product desde la CartItem para el modal.
        const product: Product = {
          id: line.productId,
          name: line.name,
          price: line.price - optionsPriceDelta(line.menuOptions),
          sku: line.sku ?? '',
          categoryId: line.category,
          brandId: line.brandId ?? null,
          itemType: line.catalogItemType === 'service' ? 'service' : 'physical',
          unitOfMeasure: line.unitOfMeasure ?? 'unit',
          sellByWeight: line.sellByWeight ?? false,
        } as Product;
        setEditingLineId(line.lineId);
        setEditingMenuOptions(line.menuOptions);
        setEditingLineNote(line.lineNote ?? null);
        // Cargar groups desde caché o fetch.
        const cached = peekCachedMenuOptions(product.id);
        if (cached && cached.length > 0) {
          setMenuOptionsProduct(product);
          setMenuOptionsGroups(cached);
          setMenuOptionsLoading(false);
          return;
        }
        setMenuOptionsProduct(product);
        setMenuOptionsGroups([]);
        setMenuOptionsLoading(true);
        try {
          const map = await fetchMenuOptionsForProducts([product.id]);
          const groups = map.get(product.id);
          if (groups && groups.length > 0) {
            setMenuOptionsGroups(groups);
            setMenuOptionsLoading(false);
            return;
          }
          // Sin modifiers — no tiene sentido editar.
          setMenuOptionsProduct(null);
          setMenuOptionsGroups([]);
          setMenuOptionsLoading(false);
          setEditingLineId(null);
          setEditingMenuOptions(undefined);
          setEditingLineNote(null);
          showInfo('Sin adicionales', 'Este producto no tiene adicionales para editar.');
        } catch {
          const disk = await readPersistedMenuOptions(product.id);
          if (disk && disk.length > 0) {
            setMenuOptionsGroups(disk);
            setMenuOptionsLoading(false);
            return;
          }
          setMenuOptionsProduct(null);
          setMenuOptionsGroups([]);
          setMenuOptionsLoading(false);
          setEditingLineId(null);
          setEditingMenuOptions(undefined);
          setEditingLineNote(null);
          showInfo('Sin conexión', 'No se pudieron cargar las opciones. Reintentá con internet.');
        }
      } finally {
        isProcessingRef.current = false;
      }
    },
    [],
  );

  // Gap 4: re-abrir el picker de variantes sobre una línea existente.
  const openVariantEditor = useCallback(
    async (line: CartItem) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      try {
        const product: Product = {
          id: line.productId,
          name: line.name.split(' — ')[0] ?? line.name,
          price: line.price,
          sku: line.sku ?? '',
          categoryId: line.category,
          brandId: line.brandId ?? null,
          itemType: line.catalogItemType === 'service' ? 'service' : 'physical',
          unitOfMeasure: line.unitOfMeasure ?? 'unit',
          sellByWeight: line.sellByWeight ?? false,
        } as Product;
        setEditingLineId(line.lineId);
        try {
          const variants = await listVariantsForProduct(product.id);
          if (variants.length > 0) {
            setVariantPickerProduct(product);
            setVariantPickerVariants(variants);
            return;
          }
          setEditingLineId(null);
          showInfo('Sin variantes', 'Este producto no tiene variantes para editar.');
        } catch (error) {
          setEditingLineId(null);
          addSentryBreadcrumb('pos', 'openVariantEditor listVariantsForProduct failed', 'warning', { productId: product.id, error: String(error) });
          showInfo('Sin conexión', 'No se pudieron cargar las variantes. Reintentá con internet.');
        }
      } finally {
        isProcessingRef.current = false;
      }
    },
    [],
  );

  const stockTapNoticeCopy = useMemo(
    () => (stockTapNotice ? buildStockTapNoticeCopy(stockTapNotice.product, stockTapNotice.policy) : null),
    [stockTapNotice]
  );

  return {
    weightProduct,
    setWeightProduct,
    hourProduct,
    setHourProduct,
    variantPickerProduct,
    setVariantPickerProduct,
    variantPickerVariants,
    setVariantPickerVariants,
    pendingVariantId,
    setPendingVariantId,
    handleVariantSelect,
    menuOptionsProduct,
    setMenuOptionsProduct,
    closeMenuOptions,
    menuOptionsGroups,
    menuOptionsLoading,
    handleMenuOptionsConfirm,
    stockTapNotice,
    setStockTapNotice,
    stockTapNoticeCopy,
    handleProductPress,
    handlePackagePress,
    handleAddOutOfStockProduct,
    openInventoryForProduct,
    addedFlashProductId,
    // Gap 4: edit mode
    openMenuOptionsEditor,
    openVariantEditor,
    editingMenuOptions,
    editingLineNote,
  };
}
